<template>
  <div
    ref="rootRef"
    class="wl-input relative isolate box-border flex h-12 items-center gap-2 overflow-hidden rounded-2xl border border-solid border-transparent transition duration-200"
    :class="[
      `variant-${variant}`,
      `type-${type}`,
      `size-${size}`,
      {
        'is-disabled': isDisabled,
        'has-error': hasError,
        'has-suffix': $slots.suffix || hasClear,
        'has-focus': hasFocus,
      },
    ]"
    @click="handleFocus"
  >
    <div v-if="$slots.prefix" class="prefix flex flex-shrink-0 items-center">
      <slot name="prefix" />
    </div>
    <div class="input-wrapper relative flex flex-1 overflow-hidden">
      <label v-if="label" :for="id" class="hidden" :aria-label="label">
        {{ label }}
      </label>
      <input
        :id="id"
        ref="inputRef"
        :name="id"
        class="font-body text-emphasis placeholder:text-muted m-0 max-w-full flex-1 appearance-none overflow-hidden bg-transparent px-0 text-sm leading-5 font-medium outline-none placeholder:font-medium placeholder:opacity-100"
        :disabled="isDisabled"
        :type="type"
        :autocomplete="autocomplete"
        :placeholder="label"
        :value="modelValue"
        :spellcheck="spellcheck"
        @input="onInput"
        @focus="onFocus"
        @blur="handleNativeBlur"
        @keydown.enter="handleBlur"
        @keydown.escape="handleBlur"
        data-lpignore="true"
        data-form-type="other"
      />
    </div>
    <div
      v-if="$slots.suffix || hasClear"
      class="suffix text-muted relative z-20 flex min-w-6 shrink-0 items-center justify-end"
    >
      <div
        v-if="$slots.suffix"
        class="content suffix-item"
        :class="{ 'is-hidden': showClear }"
      >
        <slot name="suffix" />
      </div>
      <div
        v-if="hasClear"
        class="clear suffix-item absolute inset-y-0 right-0 flex w-6 items-center justify-center"
        :class="{ 'is-hidden': !showClear }"
      >
        <WLIcon
          class="wl-input-icon-clear text-muted shrink-0 cursor-pointer transition"
          :name="IconName.NAVIGATION_CLOSE_FILLED"
          @mousedown.stop="handleClear"
        />
      </div>
    </div>
  </div>
</template>

<!-- TODO: should be replaced with vue3 library, doesn't make sense to maintain 2 inputs.-->
<script lang="ts" setup>
import { computed, onMounted, ref, toRefs } from "vue";
import {
  type InputEmits,
  type InputProps,
  InputSize,
  InputType,
  InputVariant,
} from "./types";
import { IconName, WLIcon } from "../icon";
import { onClickOutside } from "@vueuse/core";

const emit = defineEmits<InputEmits>();
const props = withDefaults(defineProps<InputProps>(), {
  type: InputType.TEXT,
  size: InputSize.SMALL,
  autocomplete: "off",
  variant: InputVariant.DEFAULT,
  selectOnFocus: false,
  iconColorOnFocus: false,
});

const {
  id,
  type,
  size,
  label,
  isDisabled,
  autocomplete,
  spellcheck,
  autoFocus,
  hasClear,
  hasError,
  variant,
  selectOnFocus,
} = toRefs(props);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const modelValue = defineModel<any>({
  set(value: string | number) {
    if (type.value === "number") {
      return Number(value) || 0;
    }

    return value;
  },
});
const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const hasFocus = ref(false);

const onInput = (event: Event): void => {
  modelValue.value = (event.target as HTMLInputElement).value;
};

onMounted(() => {
  if (autoFocus.value) {
    inputRef.value?.focus();
  }
});
const showClear = computed(() => {
  return (
    hasClear.value &&
    hasFocus.value &&
    (modelValue.value || modelValue.value === 0)
  );
});

const onFocus = (event: FocusEvent): void => {
  hasFocus.value = true;
  if (selectOnFocus.value) {
    inputRef.value?.select();
  }
  emit("focus", event);
};

const handleBlur = (event: Event): void => {
  inputRef.value?.blur();
  hasFocus.value = false;

  emit("blur", event);
};

// NOTE: Applying the above `handleBlur` to the `input` means that clicking on
// the `clear` button would remove focus from the `input`, but more importantly
// prevents it from working as a `@blur` event will trigger and be emitted
// before the `@click` event.
//
// Instead, we solve the original problem of "`focus` not being removed on tab"
// by solely removing `focus` (visually) and not triggering and re-emitting the
// native `@blur` event.
const handleNativeBlur = (): void => {
  hasFocus.value = false;
};

onClickOutside(rootRef, handleBlur);

const handleClear = (): void => {
  if (inputRef.value) {
    inputRef.value.focus();
  }

  modelValue.value = "";
  emit("clear");
};

const handleFocus = (): void => {
  if (inputRef.value) {
    inputRef.value.focus();
  }
};

defineExpose({ inputRef });
</script>

<style>
@reference "@theme";

.wl-input {
  &.size-small {
    @apply h-8 px-2;
  }

  &.size-medium {
    @apply p-3;
  }

  .input-wrapper {
    &::after {
      @apply pointer-events-none absolute inset-y-0 right-0 z-10 w-5 bg-gradient-to-r transition-all duration-200 ease-in-out content-[''];
    }
  }

  &.variant-default {
    @apply bg-muted hover:border-emphasis border border-solid border-transparent transition-colors duration-200;

    .input-wrapper {
      &::after {
        @apply to-muted from-transparent opacity-100;
      }
    }
  }

  &.variant-elevated {
    @apply bg-default shadow-down-sm transition-shadow duration-200;

    .input-wrapper {
      &::after {
        @apply to-muted from-transparent opacity-0 transition-opacity;
      }
    }
  }

  &.is-disabled {
    @apply cursor-not-allowed border-transparent opacity-20;
  }

  &.has-error {
    @apply border-accent-emphasis bg-warning-muted;
  }

  .prefix {
    @apply transition-colors duration-200;
  }

  &.has-focus {
    /*
     Fix for older versions of Safari where the outline will not follow the
     border-radius. See the changelog, specifically "Changed outline to follow
     `border-radius`" for more:
     https://webkit.org/blog/13575/release-notes-for-safari-technology-preview-157/

     Once switched over, the below code should work:
     @apply border-accent bg-default duration-100 ease-in-out outline outline-2 outline-accent-emphasis2/20;
     */
    @apply border-emphasis bg-default;

    .prefix {
      @apply text-accent;
    }

    .input-wrapper {
      &::after {
        @apply opacity-0;
      }
    }
  }

  .suffix-item {
    @apply transition duration-200 ease-out;

    &.is-hidden {
      @apply pointer-events-none translate-x-1 opacity-0 duration-150 ease-in;
    }
  }

  /*Remove Safari's input appearance*/
  input[type="search"]::-webkit-search-decoration,
  input[type="search"]::-webkit-search-cancel-button,
  input[type="search"]::-webkit-search-results-button,
  input[type="search"]::-webkit-search-results-decoration,
  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    @apply appearance-none;
  }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button {
    @apply m-0;
  }

  input[type="number"] {
    -moz-appearance: textfield;
  }
}
</style>
